import React, { createRef, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { RootState } from 'Store'
import { Col, Row } from 'antd'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import {
  formatDate,
  formatDateCustom,
  formatDateTime,
  formatDateWithoutTime,
  numberWithComma,
} from 'helpers/format'
import { TableListPage } from 'Components/common/Table'
import { PrinterOutlined } from '@ant-design/icons'
import { ActionButton } from 'Components/button'
import { ManufacturData, ManufacturList } from 'Store/minirmx-dashboard/types'
import ReactToPrint from 'react-to-print'
import CompareReportPDF from './CompareReportPDF'
import { selectCurrentUser } from 'Store/user/selector'

type Props = {
  data: ManufacturData[]
  pagination?: any
} & ReduxProps

const CompareReport: React.FunctionComponent<Props> = props => {
  const { data, pagination, currentUser } = props
  const [elRefs, setElRefs] = useState([] as any[])

  useEffect(() => {
    if (data && data.length > 0) {
      const ref = data.map((item, i) => elRefs[i] || createRef())
      setElRefs(ref)
    }
  }, [data])

  const renderTitle = (title: string, sub?: string) => {
    return (
      <div>
        <div>{title}</div>
        {sub && <div>{sub}</div>}
      </div>
    )
  }

  const columns = [
    {
      title: renderTitle('เลขที่'),
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return index + 1
      },
      width: '60px',
    },
    {
      title: renderTitle('วันที่', 'เริ่มต้น'),
      dataIndex: 'start_dt',
      key: 'start_dt',
      align: 'center',
      render: (val: string) => formatDateCustom(val, 'DD/MM/YYYY เวลา HH:mm'),
    },
    {
      title: renderTitle('วันที่', 'สิ้นสุด'),
      dataIndex: 'end_dt',
      key: 'end_dt',
      align: 'center',
      render: (val: string) => formatDateCustom(val, 'DD/MM/YYYY เวลา HH:mm'),
    },
    {
      title: renderTitle('ระยะเวลา', 'การผลิต (นาที)'),
      dataIndex: 'duration',
      key: 'duration',
      align: 'center',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('Strength', 'Number'),
      dataIndex: 'no',
      key: 'no',
      align: 'center',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('Strength', 'Class (ksc)'),
      dataIndex: 'class',
      key: 'class',
      align: 'center',
    },
    {
      title: renderTitle('น้ำหนักรวม', '(กก.)'),
      align: 'center',
      dataIndex: 'weight',
      key: 'weight',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('จำนวนการผลิต', '(คิว)'),
      align: 'center',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('ปูนซีเมนต์', '(กก.)'),
      align: 'center',
      dataIndex: 'cement',
      key: 'cement',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('ทราย', '(กก.)'),
      align: 'center',
      dataIndex: 'sand',
      key: 'sand',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('หิน', '(กก.)'),
      align: 'center',
      dataIndex: 'rock',
      key: 'rock',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: renderTitle('น้ำ', '(ลิตร)'),
      align: 'center',
      dataIndex: 'water',
      key: 'water',
      render: (val: number) => numberWithComma(val || 0),
    },
    {
      title: 'สั่งพิมพ์',
      align: 'center',
      dataIndex: 'water',
      key: 'water',
      // eslint-disable-next-line react/display-name
      render: (val: number, record: ManufacturData, index: number) => (
        <ReactToPrint
          trigger={() => {
            return (
              <ActionButton
                text=""
                icon={<PrinterOutlined />}
                label="สั่งพิมพ์"
                action="deleted"
                onClick={() => {}}
              />
            )
          }}
          content={() => elRefs[index] && elRefs[index].current}
          documentTitle={`ใบแสดงส่วนผสมการผลิตโดยประมาณ (${formatDateTime(
            record.start_dt,
          )} ถึง ${formatDateTime(record.end_dt)})`}
        />
      ),
    },
  ]

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col>
            <BoxTitle>
              รายงานเปรียบเทียบการผลิตสินค้า{' '}
              <Unit>{`หมายเหตุ: ข้อมูลปริมาณ ปูนซีเมนต์, หิน, ทราย และ น้ำ จากรายงานที่ออกโดยระบบ Mini RMX iTrack ใช้สำหรับการคำนวน Mix Design เท่านั้น บริษัทไม่รับรองและไม่มีความรับผิดใดต่อความถูกต้องและสมบูรณ์ของข้อมูล หรือการนำข้อมูลดังกล่าวไปใช้เพื่อวัตถุประสงค์อย่างอื่น`}</Unit>
            </BoxTitle>
          </Col>
        </Row>
      </Col>
      <Col md={24} xs={24} sm={24}>
        <BoxWrapper>
          <Row gutter={[48, 48]} align="top" justify="center">
            <Col span={24}>
              <TableListPage
                columns={columns}
                datas={data}
                isLoading={false}
                paginationContent={pagination}
                size="small"
                bordered={false}
                scrollX={800}
              />
            </Col>
          </Row>
        </BoxWrapper>
      </Col>
      <Col span={24}>
        {data &&
          data.map((item, i) => {
            return (
              <Row ref={elRefs[i]} key={i}>
                <Col span={24}>
                  <CompareReportPDF detail={item} merchant={currentUser?.activeMerchant} />
                </Col>
              </Row>
            )
          })}
      </Col>
    </Row>
  )
}

const BoxWrapper = styled.div`
  padding: 0px 10px;
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
  border-radius: 10px;
  border-color: transparent;
`

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body2};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const Legend = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const Unit = styled(Legend)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(CompareReport)
